/** Small presentation helpers shared by ChatSidebar and ChatMessageView. */

// On-brand purple ladder (see apiboo-auth's Avatars.Pixel for the server-side
// counterpart). Stops at #a78bfa — lighter shades don't hold enough contrast
// against the white initials text this fills a background for.
export const AVATAR_COLORS = [
  '#2b2250', '#4c3b96', '#6d54c4', '#8869eb', '#a78bfa',
];

export function initials(name) {
  const n = (name || '?').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function avatarColor(seed) {
  const s = String(seed ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

/** The Boards avatars' pastels (artboards: Samin violet, Mira green, Jonas
 *  amber), dark initials on top. */
export const PASTEL_AVATAR_COLORS = ['#c4b5fd', '#6ee7b7', '#fcd34d', '#93c5fd', '#f9a8d4', '#fdba74'];

/** A person's pastel. Numeric ids take consecutive colours (id mod 6), so the
 *  members of a small team — usually created one after the other — differ;
 *  anything else is hashed like avatarColor. */
export function pastelAvatarColor(seed) {
  const s = String(seed ?? '');
  const n = PASTEL_AVATAR_COLORS.length;
  if (/^\d+$/.test(s)) return PASTEL_AVATAR_COLORS[Number(s) % n];
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PASTEL_AVATAR_COLORS[h % n];
}

/** The other participant of a 1:1 DM (or null). */
export function dmOther(channel, currentUserId) {
  if (!channel || channel.kind !== 'dm') return null;
  return (channel.members || []).find((m) => m.user_id !== currentUserId) || null;
}

/**
 * Whose avatar a 1:1 row shows: the other person, or YOU in a note-to-self
 * conversation.
 *
 * `dmOther` answers null for a self-DM — correct for its own question ("who is
 * the other party"), and wrong as an avatar subject, because the row still
 * shows a face and that face still has a status. Passing that null to the
 * status badge is why your own row was the one row in the sidebar with no dot.
 *
 * @returns {*} a user id, or null when this is not a dm
 */
export function dmSubjectId(channel, currentUserId) {
  if (!channel || channel.kind !== 'dm') return null;
  const other = dmOther(channel, currentUserId);
  return other ? other.user_id : currentUserId ?? null;
}

/** Display name + initials/seed for any conversation kind. */
export function channelDisplay(channel, currentUserId) {
  if (!channel) return { name: 'Conversation', seed: '?' };
  if (channel.kind === 'dm') {
    const other = dmOther(channel, currentUserId);
    const name = other?.user?.name || 'Direct message';
    return { name, seed: other?.user_id || name };
  }
  if (channel.kind === 'channel') {
    return { name: `# ${channel.name || 'channel'}`, seed: `ch${channel.id}` };
  }
  // group
  const names = (channel.members || [])
    .filter((m) => m.user_id !== currentUserId)
    .map((m) => m.user?.name)
    .filter(Boolean);
  return { name: channel.name || names.join(', ') || 'Group', seed: `gr${channel.id}` };
}

/**
 * True when the query is a complete-looking email address. People lookups are
 * exact-email only (no directory browsing — mirrors the server rule): a user
 * is only discoverable by someone who already knows their full address.
 * As permissive as registration (and the server's search regex) so dotless
 * self-hosted domains like admin@localhost stay findable.
 */
export function isCompleteEmail(q) {
  return /^[^\s]+@[^\s]+$/.test((q || '').trim());
}
